import { createTheme, outlinedInputClasses } from '@mui/material';
import appTheme from './AppTheme';
import { colorPalette } from './ColorPalette';
import { FontsEnum } from '../constants/enums/FontsEnum.ts';

export const containerisedWidth = '1600px';
export const animationMediaBreakpoint = 'md';
export const mobileDesktopMediaBreakpoint = 'md';
export const tabletBreakpoint = 'md';
export const desktopBreakpoint = 'xxl';
export const navbarHeight = '64px';

export const mediaBreakpointGreaterThanMd = appTheme.breakpoints.up('md');
export const mediaBreakpointLowerThanMd = appTheme.breakpoints.down('md');

export const mobileHPadding = '16px';
export const tabletLaptopHPadding = '96px';

export const gradientBorderSpinAnimationDuration = 3;
export const gradientBorderFadeInOutAnimationDuration = 0.8;

export const scrollBarWidthPx = '14px';

// fixed header
export const appBarHeight = 53;
export const appBarHeightPx = `${appBarHeight}px`;

const appBarMobileUIHeight = 52;
export const appBarMobileUIHeightPx = `${appBarMobileUIHeight}px`;

const tabsHeaderHeight = 66;
export const tabsHeaderHeightPx = `${tabsHeaderHeight}px`;

const bestViewedOnDesktopHeight = 45;
export const bestViewedOnDesktopHeightPx = `${bestViewedOnDesktopHeight}px`;

const marketingBannerHeight = 44;
export const marketingBannerHeightPx = `${marketingBannerHeight}px`;

const footerHeight = 32;
export const footerHeightPx = `${footerHeight}px`;
export const scrollAmount = 400;

export const lipSyncSectionTitleContainerMinWidth = '280px';

const spinAnimationKeyframe = {
	'@keyframes spin': {
		'0%': {
			transform: 'rotate(0deg)',
		},
		'100%': {
			transform: 'rotate(360deg)',
		},
	},
};

export const reusableStyles = {
	mobileOnly: { display: { xs: 'flex', [tabletBreakpoint]: 'none' } },
	desktopUI: {
		width: '100%',

		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	desktopOnly: { display: { xs: 'none', [tabletBreakpoint]: 'flex' } },

	fixedScreen: {
		position: 'fixed',
		width: '100vw',
		height: '100vh',

		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',

		backgroundColor: 'colors.dark6',

		zIndex: 2000,
	},

	pageWrapper: {
		width: '100%',
		height: '100dvh',

		display: 'flex',
		flexDirection: 'column',

		transition: 'padding 0.5s ease',
		overflowY: 'auto',
	},
	// NOTE: PADDING_TOP CALCULATION
	tabContentWrapper: {
		paddingTop: {
			xs: `calc(${appBarMobileUIHeightPx} + ${tabsHeaderHeightPx} + ${bestViewedOnDesktopHeightPx})`,
			[tabletBreakpoint]: `calc(${appBarHeightPx} + ${tabsHeaderHeightPx})`,
		},
	},

	foldWrapper: {
		width: '100%',

		display: 'flex',
		justifyContent: 'center',
	},
	fold: {
		width: '100%',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
	},
	edgeToEdge: {
		width: '100%',

		paddingRight: { xs: '16px', [tabletBreakpoint]: '48px' },
		paddingLeft: { xs: '16px', [tabletBreakpoint]: '48px' },
	},

	// NOTE: PADDING_TOP CALCULATION
	pageWithoutTabsHeader: {
		flexGrow: 1,
		width: '100%',
		height: '100vh',
		paddingTop: 0,

		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	foldWithoutTabsHeader: {
		flexGrow: 1,
		alignItems: 'center',
	},
	contentWithoutTabsHeader: {
		flexGrow: 1,
		width: '100%',

		display: 'flex',
	},
	backButton: {
		flexShrink: 0,
		height: '32px',
		width: '32px',
		borderRadius: '50%',

		backgroundColor: 'transparent',

		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',

		cursor: 'pointer',

		'&:hover': {
			backgroundColor: 'colors.dark4',
		},
		'& svg': {
			height: '24px',
			width: 'auto',
		},
	},

	flexRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	flexRowWithGap: (gap) => ({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: `${gap}px`,
	}),
	flexCol: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},
	flexColWithGap: (gap) => ({
		display: 'flex',
		flexDirection: 'column',
		gap: `${gap}px`,
	}),
	flexCenter: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	verticalScrollBarStyles: {
		'&::-webkit-scrollbar': {
			display: { xs: 'none', sm: 'initial' },
			width: scrollBarWidthPx,
			height: '0',
		},
	},
	horizontalScrollBarStyles: {
		'&::-webkit-scrollbar': {
			display: { xs: 'none', sm: 'initial' },
			width: '0',
			height: `${scrollBarWidthPx}`,
		},
	},
	scrollBarStyles: {
		'&::-webkit-scrollbar': {
			display: { xs: 'none', sm: 'initial' },
			width: scrollBarWidthPx,
			height: `${scrollBarWidthPx}`,
		},
	},

	skeletonLoaderStyles: {
		height: appTheme.typography.skHeight,
		borderRadius: '3px',
		color: 'transparent !important',
		backgroundColor: 'rgba(241,241,241,0.1)',
		position: 'relative',
		overflow: 'hidden',
		'&::before': {
			height: '100%',
			width: '70%',

			content: '""',
			background: 'linear-gradient(90deg, transparent, #C7C7C7, transparent)',

			position: 'absolute',
			left: '-100%',
			top: 0,

			animation: 'skeletonLoading 2s ease-in-out infinite',
			opacity: 0.1,
			zIndex: 1,
		},

		'@keyframes skeletonLoading': {
			from: { left: '-100%' },
			to: { left: '100%' },
		},
	},

	spinAnimationStyles: {
		animation: `spin ${gradientBorderSpinAnimationDuration}s linear infinite`,

		...spinAnimationKeyframe,
	},
	spinAnimationKeyframe,

	hoverStyles: {
		position: 'relative',

		'&:hover': {
			'& .hoverWrapper': {
				visibility: 'visible',
			},
		},
	},
	threeColumnGrid: {
		lorasGrid: {
			width: '100%',
			display: 'grid',
			gridTemplateColumns: 'repeat(3, 1fr)',
			gridTemplateRows: 'auto',
			gridGap: '16px',
		},
	},

	//   Tabs
	tabsCont: {
		width: '100%',
		marginBottom: '24px',
		display: 'flex',
	},
	tab: {
		minWidth: '70px',
		padding: { mm: '8px 10px', md: '8px 24px' },
		borderWidth: '0 0 1px 0',
		borderStyle: 'solid',

		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '4px',

		cursor: 'pointer',

		'& svg': { height: '16px', width: 'auto' },
	},
	socialButton: {
		flexShrink: 0,
		height: '32px',
		width: '32px',
		minWidth: 'unset',
		borderRadius: '50%',

		backgroundColor: 'colors.dark4',
		textTransform: 'none',

		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',

		overflow: 'hidden',

		'&:hover': {
			backgroundColor: 'colors.dark4',
		},
	},
	editProfileButton: {
		marginRight: '8px',
		padding: '6px 8px',
		minWidth: 'fit-content',

		textTransform: 'none',
		display: 'flex',
		alignItems: 'center',
		gap: '4px',

		'&:hover': {
			backgroundColor: 'colors.dark3',
		},
	},
	shareButton: {
		padding: '6px 8px',
		minWidth: 'unset',
		borderRadius: '4px',

		backgroundColor: 'colors.dark3',
		textTransform: 'none',

		display: 'flex',
		alignItems: 'center',
		gap: '4px',

		'&:hover': {
			backgroundColor: 'colors.dark3',
		},
		'&: svg': {
			height: '24px',
			width: 'auto',
		},
	},
	radio: {
		'& .MuiSvgIcon-root': {
			fontSize: '28px',
		},
		color: 'colors.light5',
		'&.Mui-checked': {
			color: 'colors.light1',
		},
	},

	canvasRadio: {
		'& .MuiSvgIcon-root': {
			fontSize: '20px',
		},
		color: 'colors.light3',
		'&.Mui-checked': {
			color: 'colors.light3',
		},
	},

	popover: {
		button: {
			minWidth: '110px',
			padding: '4px 8px 4px 12px',
			border: `1px solid ${colorPalette.dark2}`,

			textTransform: 'none',

			display: 'flex',
			alignItems: 'center',
			gap: '4px',

			'& svg': { height: '16px', width: 'auto' },
		},
		popover: {
			'& .MuiPopover-paper': {
				display: 'flex',
				backgroundColor: 'colors.dark3',
			},
		},
		shareContainer: {
			minWidth: '160px',
			backgroundColor: 'colors.dark4',
			display: 'flex',
			justifyContent: 'center',
		},
		list: {
			width: '100%',
			padding: 0,
		},
		listItem: {
			width: '100%',
			padding: '24px 16px',
			display: 'flex',
			justifyContent: 'start',
			alignItems: 'center',

			cursor: 'pointer',
			'& svg': { width: '30px', height: 'auto' },
		},
		divider: {
			width: '100%',
			borderBottom: `1px solid ${colorPalette.dark1}`,
		},
	},
	pageHLeftAlignedHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '4px',

		'& svg': { height: '20px', width: 'auto' },
	},
	pageLayout: {
		flexGrow: 1,
		width: '100%',
		height: '100%',

		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
	pageContainer: {
		width: '100%',
		alignItems: 'start',
		justifyContent: 'space-between',
	},
	uploadImageContainer: {
		position: 'relative',
		width: '100%',
	},
	uploadPlaceholderImage: {
		width: 'inherit',
		maxWidth: '100%',
		objectFit: 'contain',
		cursor: 'pointer',
	},
	greenButton: {
		border: `1px solid ${colorPalette.buttonGreen}`,
		color: 'colors.dark6',
		backgroundColor: 'colors.buttonGreen',
		'&:hover, &:focus': {
			backgroundColor: 'colors.drunkenDragonfly100',
		},
		'&.Mui-disabled': {
			color: 'colors.dark2',
			backgroundColor: 'colors.light5',
		},
	},
	blackButton: {
		border: `1px solid ${colorPalette.light5}`,
		color: 'colors.light3',
		backgroundColor: 'colors.dark5',
		'&:hover, &:focus': {
			backgroundColor: 'colors.dark6',
		},
		'&.Mui-disabled': {
			color: 'colors.dark2',
			backgroundColor: 'colors.light5',
		},
	},
	changePhotoButton: {
		width: '100%',
		padding: '8px',
		borderRadius: '4px',

		color: 'colors.light2',
		backgroundColor: 'colors.dark2',
		border: `1px solid ${colorPalette.dark1}`,
		textTransform: 'none',

		'&:hover': {
			backgroundColor: 'colors.dark2',
		},
	},
	greenFilled: {
		padding: '8px 16px',
		borderRadius: '4px',
		backgroundColor: 'colors.buttonGreen',
		textTransform: 'none',
		cursor: 'pointer',
		'&:hover, &:focus': {
			backgroundColor: 'colors.buttonGreen',
		},
		'&.Mui-disabled': {
			color: 'colors.dark2',
			backgroundColor: 'colors.light5',
		},
	},
	greenOutline: {
		padding: '8px 16px',
		borderRadius: '4px',
		backgroundColor: 'colors.dark6',
		border: `1px solid ${colorPalette.buttonGreen}`,
		'&:hover, &:focus': {
			backgroundColor: 'colors.dark6',
		},
		textTransform: 'none',
		cursor: 'pointer',
	},
	light5Outlined: {
		padding: '8px 16px',
		borderRadius: '4px',
		backgroundColor: 'colors.dark6',
		border: `1px solid ${colorPalette.light5}`,
		textTransform: 'none',
		'&:hover': {
			backgroundColor: 'colors.dark6',
		},
		cursor: 'pointer',
	},
	absoluteTopFlex: {
		display: 'flex',
		position: 'absolute',
		left: '50%',
		transform: 'translateX(-50%)',
	},
	absoluteCenterFlex: {
		display: 'flex',
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%,-50%)',
	},
};

export const customTheme = () =>
	createTheme({
		components: {
			MuiOutlinedInput: {
				styleOverrides: {
					notchedOutline: {
						borderColor: '#383838',
					},
					root: {
						[`&:hover .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: '#383838',
						},
						[`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
							borderColor: '#383838',
						},
					},
					input: {
						fontFamily: FontsEnum.SATOSHI,
						fontSize: '14px',
						fontWeight: 400,
						lineHeight: '24px',
						letterSpacing: '0.1px',
						color: '#383838',
					},
				},
			},
		},
	});
